<template>
    <v-app id="app_pending_reversals">
        <v-container fluid>
            <v-row class="pl-2 pr-6 align-center">
                <v-col cols="12" md="7" class="mt-3">
                    <p class="custom_page_main_title">PENDING PAYMENT REVERSALS</p>
                    <p class="custom_page_main_sub_title">Evaluate & authorize requests for reversal.</p>
                </v-col>
                <v-col cols="12" md="5" class="px-4 mt-5">
                    <v-row class="custom_tool_bar_style justify-end">
                        <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
                        <v-btn class="gray-text sharp-edge" size="x-large" variant="text" @click="backPageRoute()">
                            <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
                        </v-btn>
                    </v-row>
                </v-col>
            </v-row>

            <v-card class="custom_card_box mt-8 sharp-edge" elevation="1" border="sm opacity-1">
                <v-card-text class="custom_tool_bar_style">
                    <v-btn class="custom_pdf_btn sharp-edge" variant="outlined" @click="exportPDF">
                        <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
                    </v-btn>
                    <v-btn class="custom_excel_btn ml-4 sharp-edge" variant="outlined" @click="exportExcel">
                        <v-icon size="large">mdi-file-excel</v-icon>&nbsp;&nbsp;Excel
                    </v-btn>
                </v-card-text>

                <v-divider thickness="2"></v-divider>

                <v-expansion-panels v-model="panelOpen" multiple class="sharp-edge">
                    <v-expansion-panel value="filters" elevation="0" class="sharp-edge">
                        <v-expansion-panel-title expand-icon="mdi-menu-down">
                            <v-icon class="mr-3">mdi-filter-menu-outline</v-icon>
                            <span class="text-body-1 font-weight-bold">FILTERS</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row class="mt-2" dense>
                                <!-- Date Range -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-date-input v-model="dates" prepend-inner-icon="$calendar" prepend-icon=""
                                        label="Select Date Range" multiple="range" density="comfortable"
                                        class="custom_input_field_color sharp-input" variant="outlined"
                                        @click:append-inner="clearDateRange"></v-date-input>
                                </v-col>

                                <!-- Area - API-based -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectDistrict" :items="areaLists" item-title="text"
                                        item-value="value" label="Area" placeholder="Select area" variant="outlined"
                                        density="comfortable" class="custom_input_field_color sharp-input" multiple
                                        clearable :loading="areasLoading">
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable>
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Sales Rep -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-select v-model="selectedAgent" :items="agentListsWithDetails"
                                        density="comfortable" clearable item-title="text" item-value="value"
                                        class="custom_input_field_color sharp-input" label="Sales Rep" multiple
                                        placeholder="Select sales representative" variant="outlined"
                                        :loading="salesRepsLoading">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="undefined">
                                                <template v-slot:prepend>
                                                    <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                                                </template>
                                                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                                                <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                                                    REG NO: {{ item.raw.registration }}
                                                </v-list-item-subtitle>
                                            </v-list-item>
                                        </template>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable
                                                close-icon="mdi-close-circle">
                                                {{ item.raw.text }}
                                            </v-chip>
                                        </template>
                                    </v-select>
                                </v-col>

                                <!-- Route -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedRoute" :items="routeTypeOptions" item-title="text"
                                        item-value="value" label="Route" placeholder="Select route" variant="outlined"
                                        density="comfortable" class="custom_input_field_color" multiple clearable>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable>
                                                {{ item.title || item.text }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Dealer - API-based -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedDealer" :items="dealerListsWithDetails" clearable
                                        density="comfortable" item-title="text" item-value="value" label="Dealer"
                                        class="custom_input_field_color sharp-input" multiple
                                        placeholder="Select dealer" variant="outlined" :loading="dealersLoading">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="undefined">
                                                <template v-slot:prepend>
                                                    <v-checkbox-btn :model-value="props.selected"></v-checkbox-btn>
                                                </template>
                                                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
                                                <v-list-item-subtitle style="color: #01579B; font-size: 11px;">
                                                    REG NO: {{ item.raw.registration }}
                                                </v-list-item-subtitle>
                                            </v-list-item>
                                        </template>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable>
                                                {{ item.raw.text }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Collection Type -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedCollectionType" :items="collectionTypeOptions"
                                        item-title="text" item-value="value" label="Collection Type"
                                        placeholder="Select type" variant="outlined" density="comfortable"
                                        class="custom_input_field_color sharp-input" multiple clearable>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable>
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Piyestra Company -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-autocomplete v-model="selectedCompany" :items="hardcodedCompanyLists"
                                        item-title="text" item-value="value" label="Piyestra Company"
                                        placeholder="Select company" variant="outlined" density="comfortable"
                                        class="custom_input_field_color sharp-input" multiple clearable>
                                        <template v-slot:chip="{ props, item }">
                                            <v-chip v-bind="props" color="#e7e7e7" variant="flat"
                                                class="text-black font-weight-medium" size="small" closable>
                                                {{ item.title }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Search -->
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field v-model="search" clearable label="Search"
                                        placeholder="Search by Dealer, Receipt ID, Invoice, or Reason"
                                        variant="outlined" density="comfortable"
                                        class="custom_input_field_color sharp-input"></v-text-field>
                                </v-col>
                            </v-row>

                            <v-divider class="mb-3 mt-2"></v-divider>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>

                <v-card-text>
                    <!-- Loading Indicator -->
                    <v-row v-if="loading" justify="center" class="py-8">
                        <v-progress-circular indeterminate color="#800080"></v-progress-circular>
                    </v-row>

                    <!-- No Data Message -->
                    <v-row v-else-if="reversals.length === 0" justify="center" class="py-8">
                        <v-col cols="12" class="text-center">
                            <v-icon size="64" color="grey-lighten-1">mdi-inbox-outline</v-icon>
                            <p class="text-h6 text-grey mt-4">No pending reversals found</p>
                        </v-col>
                    </v-row>

                    <!-- Reversal Cards -->
                    <v-row v-else>
                        <v-col v-for="(item, index) in reversals" :key="item.id || index" cols="12" md="4"
                            class="mb-4">
                            <v-card class="reversal-card sharp-edge" elevation="2" border="sm opacity-1">
                                <v-card-text class="pa-4 card-content">
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-h6 font-weight-bold">{{ item.dealer_name || 'N/A' }}</span>
                                        <span class="text-h6 font-weight-bold purple-text">Rs. {{
                                            formatNumber(item.collect_total_amount || item.amount || 0) }}</span>
                                    </div>

                                    <div class="d-flex align-center mb-2">
                                        <span class="text-body-2 text-grey-darken-1 area-invoice-text">
                                            <span class="invoice-dot">{{ item.receipt_id || 'N/A' }}</span>
                                        </span>
                                    </div>

                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-clock-outline</v-icon>
                                            <span class="text-body-2">{{ formatTime(item.created_at) }}</span>
                                        </div>
                                    </div>

                                    <div class="d-flex justify-space-between align-center mb-3">
                                        <div class="d-flex align-center">
                                            <v-icon size="small" color="grey-darken-1"
                                                class="mr-1">mdi-calendar-outline</v-icon>
                                            <span class="text-body-2">Collection Date</span>
                                        </div>
                                        <span class="text-body-2 font-weight-medium">{{ formatDate(item.created_at) }}</span>
                                    </div>

                                    <v-card class="payment-type-box pa-3 mb-3 sharp-edge" variant="flat" elevation="0">
                                        <div class="d-flex align-center">
                                            <v-icon size="large" :color="getPaymentIconColor(item.payment_method)"
                                                class="mr-3">
                                                {{ getPaymentIcon(item.payment_method) }}
                                            </v-icon>
                                            <div>
                                                <div class="text-body-2 font-weight-bold">{{
                                                    getPaymentTypeText(item.payment_method) }}
                                                </div>
                                                <div class="text-caption text-grey-darken-1">
                                                    Status: {{ item.payment_status || 'N/A' }}
                                                </div>
                                            </div>
                                        </div>
                                    </v-card>

                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <div class="text-body-1 font-weight-bold">Reason for Reversal</div>
                                        <v-btn class="more-details-btn sharp-edge" variant="text" size="small"
                                            @click="navigateToCollectionDetail(item)" :ripple="false">
                                            More Details
                                        </v-btn>
                                    </div>

                                    <v-card class="reason-box pa-3 mb-2 reason-fixed-height sharp-edge"
                                        variant="outlined" elevation="0" border="sm opacity-1">
                                        <span class="text-body-2 reason-text">{{ item.reversal_note || 'No reason specified' }}</span>
                                    </v-card>
                                </v-card-text>

                                <v-btn class="approve-btn fixed-height-btn sharp-edge" block variant="flat"
                                    @click="approveReversal(item)">
                                    APPROVE & SEND
                                </v-btn>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Pagination (only show if there are items) -->
                    <div v-if="totalItems > 0" class="d-flex justify-end align-center mt-4">
                        <div class="d-flex align-center">
                            <span class="text-body-2 mr-2">Rows per page:</span>
                            <v-select v-model="itemsPerPage" :items="[3, 6, 9, 12]" variant="outlined" density="compact"
                                style="width: 80px;" hide-details class="mr-4 sharp-input" @update:model-value="handleItemsPerPageChange"></v-select>
                            <span class="text-body-2 mr-4">
                                {{ fromItem }}-{{ toItem }} of {{ totalItems }}
                            </span>
                            <v-btn icon="mdi-chevron-left" variant="text" :disabled="currentPage === 1"
                                @click="previousPage"></v-btn>
                            <v-btn icon="mdi-chevron-right" variant="text"
                                :disabled="currentPage === lastPage || lastPage === 0"
                                @click="nextPage"></v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>

            <v-dialog v-model="approveDialog" max-width="600" persistent>
                <v-card class="sharp-edge">
                    <div class="pa-4">
                        <v-card color="#F0F0F0" flat class="sharp-edge">
                            <template v-slot:prepend>
                                <v-card-title class="text-subtitle-1 font-weight-bold">APPROVE REVERSAL</v-card-title>
                            </template>
                            <v-spacer></v-spacer>
                            <template v-slot:append>
                                <v-btn @click="approveDialog = false" icon="mdi-close" size="x-small"
                                    class="sharp-edge"></v-btn>
                            </template>
                        </v-card>

                        <v-card color="white" flat class="mt-3 sharp-edge">
                            <v-card-item>
                                <div class="text-body-2 font-weight-medium mb-1" style="color: #424242;">Sales Rep
                                    Reason</div>
                                <v-card v-if="selectedItem" class="pa-3 mb-4 sharp-edge"
                                    style="background-color: #FFE5B4 !important;" elevation="0">
                                    <div class="text-body-2 font-weight-medium" style="color: #800080;">{{
                                        selectedItem.reversal_note || 'No reason specified' }}
                                    </div>
                                    <div class="text-caption mt-2" style="color: #757575;">{{ formatTime(selectedItem.created_at)
                                    }} | {{ formatDate(selectedItem.created_at) }}</div>
                                </v-card>

                                <v-card-title class="text-body-1 font-weight-medium mb-3 pa-0">
                                    <v-icon color="warning" size="small">mdi-alert</v-icon>
                                    &nbsp;&nbsp;Confirm approval of this payment reversal?
                                </v-card-title>

                                <div class="text-body-2 font-weight-medium mb-2" style="color: #424242;">Feedback</div>

                                <v-card class="mb-2 sharp-edge" elevation="0" border="sm opacity-1">
                                    <v-card-text class="pa-2 d-flex align-center">
                                        <v-btn icon="mdi-undo" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-redo" variant="text" size="small"
                                            class="mr-2 sharp-edge"></v-btn>
                                        <v-divider vertical class="mr-2" style="height: 24px;"></v-divider>
                                        <v-btn icon="mdi-format-bold" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-format-italic" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-format-underline" variant="text" size="small"
                                            class="mr-2 sharp-edge"></v-btn>
                                        <v-divider vertical class="mr-2" style="height: 24px;"></v-divider>
                                        <v-btn icon="mdi-format-align-left" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-format-align-center" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-format-align-right" variant="text" size="small"
                                            class="mr-1 sharp-edge"></v-btn>
                                        <v-btn icon="mdi-format-align-justify" variant="text" size="small"
                                            class="sharp-edge"></v-btn>
                                    </v-card-text>
                                </v-card>

                                <v-textarea v-model="feedbackText" variant="outlined"
                                    placeholder="Type your feedback here..." rows="4" class="mb-4 sharp-input"
                                    hide-details></v-textarea>

                                <v-divider class="mt-4 mb-4"></v-divider>

                                <div class="float-end">
                                    <v-btn background color="#800080" @click="processApproval"
                                        :loading="approving">YES</v-btn>
                                    <v-btn class="ml-3 custom_btn_secondary sharp-edge"
                                        @click="approveDialog = false">NO</v-btn>
                                </div>
                            </v-card-item>
                        </v-card>
                    </div>
                </v-card>
            </v-dialog>

            <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" location="top right" elevation="4"
                class="sharp-edge">
                <div class="d-flex align-center font-weight-medium">
                    <v-icon class="mr-2" color="white">{{ snackbarIcon }}</v-icon>
                    {{ snackbarText }}
                </div>
                <template v-slot:actions>
                    <v-btn color="white" variant="text" @click="snackbar = false" class="sharp-edge">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </template>
            </v-snackbar>
        </v-container>
    </v-app>
</template>

<script>
import axios from 'axios';
import { mapActions } from "vuex";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getPermissions } from "@/plugins/permissions";
import _ from 'lodash';

export default {
    data() {
        return {
            panelOpen: ['filters'],

            // Filter fields
            dates: [],
            search: '',
            selectedAgent: [],
            selectedRoute: [],
            selectedDealer: [],
            selectDistrict: [],
            selectedCollectionType: [],
            selectedCompany: [],

            // Loading states
            loading: false,
            approving: false,
            salesRepsLoading: false,
            dealersLoading: false,
            areasLoading: false,

            // Data options
            agentListsWithDetails: [],
            routeTypeOptions: [
                { text: 'Assign Route Plan', value: 'Assign Route Plan' },
                { text: 'Direct', value: 'Direct' },
                { text: 'Own Route Plan', value: 'Own Route Plan' },
            ],
            dealerListsWithDetails: [],
            areaLists: [],

            hardcodedCompanyLists: [
                { text: 'Piyestra Furniture (Pvt) Ltd', value: 'Piyestra Furniture (Pvt) Ltd', title: 'Piyestra Furniture (Pvt) Ltd' },
                { text: 'D.R.Industries (Pvt) Ltd', value: 'D.R.Industries (Pvt) Ltd', title: 'D.R.Industries (Pvt) Ltd' },
                { text: 'D.R.Home Appliances (Pvt) Ltd', value: 'D.R.Home Appliances (Pvt) Ltd', title: 'D.R.Home Appliances (Pvt) Ltd' }
            ],

            collectionTypeOptions: [
                { text: 'CASH', value: 'CASH', title: 'CASH' },
                { text: 'BANK', value: 'BANK', title: 'BANK' },
                { text: 'CHEQUE', value: 'CHEQUE', title: 'CHEQUE' },
                { text: 'CASH + CREDIT MEMO', value: 'CASH + CREDIT MEMO', title: 'CASH + CREDIT MEMO' },
            ],

            // Pagination from API
            currentPage: 1,
            itemsPerPage: 10,
            totalItems: 0,
            lastPage: 1,
            fromItem: 0,
            toItem: 0,

            // Dialog
            approveDialog: false,
            selectedItem: null,
            feedbackText: '',
            baseURL: process.env.VUE_APP_API_BASE_URL,

            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            snackbarIcon: 'mdi-check-circle',

            reversals: [],
            permissions: {},

            // Axios cancellation token
            cancelTokenSource: null,
        }
    },

    computed: {
        totalPages() {
            return this.lastPage;
        }
    },

    watch: {
        // All filters now trigger API call with debouncing
        search: {
            handler: _.debounce(function () {
                console.log('🔎 Search watcher triggered - calling API with search param:', this.search);
                this.currentPage = 1;
                this.fetchReversalsData();
            }, 500),
        },

        selectedAgent: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        selectedRoute: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        selectedDealer: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        selectDistrict: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        selectedCollectionType: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        selectedCompany: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
        dates: {
            handler() {
                this.currentPage = 1;
                this.fetchReversalsData();
            },
            deep: true
        },
    },

    methods: {
        ...mapActions(['updateSharedDataCollection']),

        formatNumber(value) {
            return parseFloat(value || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
        },

        formatTime(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        },

        formatDateForAPI(dateString) {
            if (!dateString) return null;

            // If it's already a Date object
            if (dateString instanceof Date) {
                const year = dateString.getFullYear();
                const month = String(dateString.getMonth() + 1).padStart(2, '0');
                const day = String(dateString.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // If it's a string
            if (typeof dateString === 'string') {
                // Try parsing it
                const date = new Date(dateString);
                if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            return null;
        },

        clearDateRange() {
        this.dates = [];
        // Immediately trigger a refresh
        this.currentPage = 1;
        this.fetchReversalsData();
    },

        backPageRoute() {
            // Use browser's history to go back to the previous page
            this.$router.go(-1);
        },

        getPaymentIcon(paymentMethod) {
            if (!paymentMethod) return 'mdi-cash';
            const method = paymentMethod.toLowerCase();
            if (method.includes('cash')) {
                return 'mdi-cash';
            } else if (method.includes('cheque')) {
                return 'mdi-file-document-outline';
            } else if (method.includes('bank')) {
                return 'mdi-bank';
            } else {
                return 'mdi-cash-multiple';
            }
        },

        getPaymentIconColor(paymentMethod) {
            if (!paymentMethod) return '#4CAF50';
            const method = paymentMethod.toLowerCase();
            if (method.includes('cash')) {
                return '#4CAF50';
            } else if (method.includes('cheque')) {
                return '#2196F3';
            } else if (method.includes('bank')) {
                return '#800080';
            } else {
                return '#FF9800';
            }
        },

        getPaymentTypeText(paymentMethod) {
            if (!paymentMethod) return 'Cash Payment';
            const method = paymentMethod.toLowerCase();
            if (method.includes('cash')) {
                return 'Cash Payment';
            } else if (method.includes('cheque')) {
                return 'Cheque Payment';
            } else if (method.includes('bank')) {
                return 'Bank Deposit';
            } else {
                return paymentMethod;
            }
        },

        navigateToCollectionDetail(item) {
            sessionStorage.setItem('return_to_pending_reversals', 'true');

            if (item && item.receipt_id) {
                this.$router.push(`/total-collections-view/${item.receipt_id}`);
            } else {
                console.error('No valid receipt_id found for navigation', item);
                this.showNotification('Could not navigate to collection details. Invalid receipt ID.', 'error', 'mdi-alert-circle');
            }
        },

        approveReversal(item) {
            this.selectedItem = item;
            this.feedbackText = '';
            this.approveDialog = true;
        },

        showNotification(text, color, icon) {
            this.snackbarText = text;
            this.snackbarColor = color;
            this.snackbarIcon = icon;
            this.snackbar = true;
        },

        async processApproval() {
            if (!this.selectedItem) return;

            this.approving = true;
            try {
                const token = localStorage.getItem('token');

                const payload = {
                    id: this.selectedItem.id,
                    comment: this.feedbackText
                };

                await axios.post(`${this.baseURL}/api/reversal-receipt/approve`, payload, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Remove the approved item from the list
                const index = this.reversals.findIndex(r => r.id === this.selectedItem.id);
                if (index !== -1) {
                    this.reversals.splice(index, 1);
                    this.totalItems--;
                }

                this.approveDialog = false;
                this.selectedItem = null;
                this.feedbackText = '';

                this.showNotification('Reversal approved successfully!', 'success', 'mdi-check-circle');
            } catch (error) {
                console.error('Error approving reversal:', error);
                this.showNotification('There was an error approving the reversal. Please try again.', 'error', 'mdi-alert-circle');
            } finally {
                this.approving = false;
            }
        },

        exportPDF() {
            const doc = new jsPDF();
            doc.text('Pending Payment Reversals', 14, 10);

            const tableData = this.reversals.map((item, index) => [
                index + 1,
                item.dealer_name || 'N/A',
                item.branch_name || 'N/A',
                item.receipt_id || 'N/A',
                `Rs. ${this.formatNumber(item.collect_total_amount || 0)}`,
                this.formatDate(item.created_at),
                this.getPaymentTypeText(item.payment_method),
                item.reversal_note || 'N/A'
            ]);

            const tableColumns = ['#', 'Dealer', 'Area', 'Receipt ID', 'Amount', 'Date', 'Payment Type', 'Reason'];

            doc.autoTable({
                head: [tableColumns],
                body: tableData,
            });

            doc.save('pending_reversals.pdf');
            this.logDocumentDownload('pending_reversals.pdf', 'pdf');
        },

        exportExcel() {
            const tableData = this.reversals.map((item, index) => ({
                '#': index + 1,
                'Dealer': item.dealer_name || 'N/A',
                'Area': item.branch_name || 'N/A',
                'Receipt ID': item.receipt_id || 'N/A',
                'Reversal Receipt ID': item.reversal_receipt_id || 'N/A',
                'Amount': `Rs. ${this.formatNumber(item.collect_total_amount || 0)}`,
                'Date': this.formatDate(item.created_at),
                'Payment Type': this.getPaymentTypeText(item.payment_method),
                'Payment Status': item.payment_status || 'N/A',
                'Reason': item.reversal_note || 'N/A',
                'Approval Status': item.reversal_approval_status || 'N/A'
            }));

            const worksheet = XLSX.utils.json_to_sheet(tableData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Pending Reversals');
            XLSX.writeFile(workbook, 'pending_reversals.xlsx');
            this.logDocumentDownload('pending_reversals.xlsx', 'excel');
        },

        async logDocumentDownload(fileName, fileType) {
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId');

                await axios.post(`${this.baseURL}/api/log-document-download`, {
                    user_id: userId,
                    file_type: fileType,
                    file_name: fileName,
                    entity_area: 'Pending Reversals',
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } catch (error) {
                console.error('Error logging download:', error);
            }
        },

        async fetchAreas() {
            try {
                this.areasLoading = true;
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId') || 1;

                const url = `${this.baseURL}/api/area/user/${userId}`;

                const response = await axios.get(url, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
                    this.areaLists = response.data.data.map(areaCode => ({
                        text: areaCode,
                        value: areaCode,
                        title: areaCode,
                    })).sort((a, b) => a.text.localeCompare(b.text));
                } else {
                    this.areaLists = [];
                }
            } catch (error) {
                console.error('Error fetching areas:', error);
                this.areaLists = [];
            } finally {
                this.areasLoading = false;
            }
        },

        async fetchSalesRepsFromAPI() {
            try {
                this.salesRepsLoading = true;
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId') || 1;

                const url = `${this.baseURL}/api/getSalesRepByUserAreaCodes?userId=${userId}`;

                const response = await axios.get(url, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data.success && response.data.data) {
                    this.agentListsWithDetails = response.data.data.map(rep => ({
                        text: rep.salesRep_name,
                        value: rep.id,
                        registration: rep.userCode,
                        id: rep.id,
                        userCode: rep.userCode,
                        email: rep.email,
                        areaCodes: rep.area_codes,
                    })).sort((a, b) => a.text.localeCompare(b.text));
                } else {
                    this.agentListsWithDetails = [];
                }
            } catch (error) {
                console.error('Error fetching sales reps:', error);
                this.agentListsWithDetails = [];
            } finally {
                this.salesRepsLoading = false;
            }
        },

        async fetchDealersFromAPI() {
            try {
                this.dealersLoading = true;
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId') || 1;

                const url = `${this.baseURL}/api/getDealersByUserAreaCodes?userId=${userId}`;

                const response = await axios.get(url, {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data.success && response.data.data) {
                    this.dealerListsWithDetails = response.data.data.map(dealer => ({
                        text: dealer.dealer_name,
                        value: dealer.dealer_code,
                        registration: dealer.dealer_code,
                    })).sort((a, b) => a.text.localeCompare(b.text));
                } else {
                    this.dealerListsWithDetails = [];
                }
            } catch (error) {
                console.error('Error fetching dealers:', error);
                this.dealerListsWithDetails = [];
            } finally {
                this.dealersLoading = false;
            }
        },

        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.fetchReversalsData();
            }
        },

        nextPage() {
            if (this.currentPage < this.lastPage) {
                this.currentPage++;
                this.fetchReversalsData();
            }
        },

        handleItemsPerPageChange() {
            this.currentPage = 1;
            this.fetchReversalsData();
        },

        async fetchReversalsData() {
            // Cancel previous request if still pending
            if (this.cancelTokenSource) {
                console.log('🛑 Cancelling previous request...');
                this.cancelTokenSource.cancel('New search initiated');
            }

            this.cancelTokenSource = axios.CancelToken.source();
            this.loading = true;

            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem("userId");
                
                // Build query parameters
                const params = {
                    page: this.currentPage,
                    size: this.itemsPerPage,
                    userId: userId 
                };

                // ✅ SEARCH FILTER
                if (this.search && this.search.trim().length > 0) {
                    params.search = this.search.trim();
                    console.log('🔍 Search parameter added:', params.search);
                }

                // ✅ DATE RANGE FILTER - FIXED
                if (this.dates && this.dates.length > 0) {
                    console.log('📅 Raw dates array:', this.dates);
                    
                    // If we have exactly 2 dates (range)
                    if (this.dates.length === 2) {
                        const startDate = this.formatDateForAPI(this.dates[0]);
                        const endDate = this.formatDateForAPI(this.dates[1]);
                        
                        if (startDate) {
                            params.start_date = startDate;
                            console.log('📅 Start date added:', startDate);
                        }
                        if (endDate) {
                            params.end_date = endDate;
                            console.log('📅 End date added:', endDate);
                        }
                    } 
                    // If we have a single date
                    else if (this.dates.length === 1) {
                        const singleDate = this.formatDateForAPI(this.dates[0]);
                        if (singleDate) {
                            params.start_date = singleDate;
                            params.end_date = singleDate;
                            console.log('📅 Single date added:', singleDate);
                        }
                    }
                }

                // ✅ AREA FILTER
                if (this.selectDistrict && this.selectDistrict.length > 0) {
                    params.area_codes = this.selectDistrict.join(',');
                    console.log('📍 Area codes added:', params.area_codes);
                }

                // ✅ SALES REP FILTER
                if (this.selectedAgent && this.selectedAgent.length > 0) {
                    params.sales_rep_ids = this.selectedAgent.join(',');
                    console.log('👤 Sales rep IDs added:', params.sales_rep_ids);
                }

                // ✅ ROUTE FILTER
                if (this.selectedRoute && this.selectedRoute.length > 0) {
                    params.route_types = this.selectedRoute.join(',');
                    console.log('🚚 Route types added:', params.route_types);
                }

                // ✅ DEALER FILTER
                if (this.selectedDealer && this.selectedDealer.length > 0) {
                    params.dealer_codes = this.selectedDealer.join(',');
                    console.log('🏢 Dealer codes added:', params.dealer_codes);
                }

                // ✅ COLLECTION TYPE FILTER
                if (this.selectedCollectionType && this.selectedCollectionType.length > 0) {
                    params.collection_types = this.selectedCollectionType.join(',');
                    console.log('💳 Collection types added:', params.collection_types);
                }

                // ✅ COMPANY FILTER
                if (this.selectedCompany && this.selectedCompany.length > 0) {
                    params.companies = this.selectedCompany.join(',');
                    console.log('🏭 Companies added:', params.companies);
                }

                console.log('📤 Final API Parameters:', params);
                console.log('🌐 API URL:', `${this.baseURL}/api/reversal-receipt/v1/pending`);

                const response = await axios.get(`${this.baseURL}/api/reversal-receipt/v1/pending`, {
                    params,
                    cancelToken: this.cancelTokenSource.token,
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                console.log('📥 API Response:', response.data);

                if (response.data && response.data.success) {
                    const responseData = response.data;
                    
                    // Set pagination info
                    this.currentPage = responseData.pagination?.current_page || 1;
                    this.totalItems = responseData.pagination?.total || 0;
                    this.lastPage = responseData.pagination?.last_page || 1;
                    this.fromItem = responseData.pagination?.from || 0;
                    this.toItem = responseData.pagination?.to || 0;
                    
                    // Map API data to reversals array
                    this.reversals = (responseData.data || []).map(item => {
                        return {
                            ...item,
                            dealer_name: item.dealer_name || 'N/A',
                            receipt_id: item.receipt_id || 'N/A',
                            reversal_receipt_id: item.reversal_receipt_id || 'N/A',
                            payment_method: item.payment_method || 'N/A',
                            payment_status: item.payment_status || 'N/A',
                            collect_total_amount: item.collect_total_amount || '0',
                            receipt_status: item.receipt_status || 'N/A',
                            bank_name: item.bank_name || null,
                            branch_name: item.branch_name || 'N/A',
                            reversal_note: item.reversal_note || 'No reason specified',
                            reversal_approval_Comment: item.reversal_approval_Comment || null,
                            overdue_approval_status: item.overdue_approval_status || 'PENDING',
                            reversal_approval_status: item.reversal_approval_status || 'PENDING',
                            image1: item.image1 || null,
                            image2: item.image2 || null,
                            route_type: item.route_type || 'Direct',
                            route_id: item.route_id || null,
                            created_at: item.created_at || null,
                            updated_at: item.updated_at || null,
                        };
                    });
                    
                    console.log(`✅ Loaded ${this.reversals.length} reversals (Page ${this.currentPage} of ${this.lastPage}, Total: ${this.totalItems})`);
                } else {
                    // Handle case where success is false but we have a specific error message
                    const errorMsg = response.data?.message || '';
                    console.warn('Unexpected API response format:', response.data);
                    
                    if (errorMsg.toLowerCase().includes('only area managers can view this list') || 
                        errorMsg.toLowerCase().includes('area manager')) {
                        this.showNotification('Only Area Managers can view this list', 'error', 'mdi-alert-circle');
                    } else {
                        this.showNotification('Error fetching reversal data. Please try again.', 'error', 'mdi-alert-circle');
                    }
                    
                    this.reversals = [];
                    this.totalItems = 0;
                    this.lastPage = 1;
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log('⚠️ Request cancelled:', error.message);
                } else {
                    console.error('Error fetching API reversals:', error);
                    
                    // Check if error response contains the specific message
                    let errorMessage = 'Error fetching reversal data. Please try again.';
                    
                    if (error.response && error.response.data) {
                        const responseData = error.response.data;
                        const responseMsg = responseData.message || responseData.error || '';
                        
                        if (responseMsg.toLowerCase().includes('only area managers can view this list') || 
                            responseMsg.toLowerCase().includes('area manager')) {
                            errorMessage = 'Only Area Managers can view this list';
                        }
                    }
                    
                    // Also check the error message itself
                    if (error.message && (
                        error.message.toLowerCase().includes('only area managers can view this list') ||
                        error.message.toLowerCase().includes('area manager')
                    )) {
                        errorMessage = 'Only Area Managers can view this list';
                    }
                    
                    this.reversals = [];
                    this.totalItems = 0;
                    this.lastPage = 1;
                    this.showNotification(errorMessage, 'error', 'mdi-alert-circle');
                }
            } finally {
                this.loading = false;
            }
        }
    },

    mounted() {
        const dashboardId = 10;
        this.permissions = getPermissions(dashboardId);

        // Load all filter data
        this.fetchSalesRepsFromAPI();
        this.fetchDealersFromAPI();
        this.fetchAreas();
        this.fetchReversalsData();
    },

    beforeUnmount() {
        // Cancel pending requests when component unmounts
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('Component unmounting');
        }
    }
}
</script>

<style lang="scss" scoped>
#app_pending_reversals {
    .sharp-edge {
        border-radius: 2px !important;
    }

    ::v-deep .sharp-input .v-field {
        border-radius: 2px !important;
    }

    .custom_font_color_view {
        color: #156CF7 !important;
    }

    .custom_font_color_edit {
        color: #2A8200 !important;
    }

    .custom_font_color_delete {
        color: #BB0000 !important;
    }

    .custom_company_logo_nav {
        width: 110px;
        height: 35px;
    }

    .custom_edit_btn {
        color: #1B5E20 !important;
        font-size: var(--font_size_12) !important;
        font-family: var(--font-family) !important;
        border: 2px #77AF5C solid !important;
        background: #C8E6C9 !important;
        border-radius: 2px !important;
    }

    .custom_delete_btn {
        color: #BB0000 !important;
        font-size: var(--font_size_12) !important;
        font-family: var(--font-family) !important;
        border: 2px #D76C6C solid !important;
        background: #F5D9D9 !important;
        border-radius: 2px !important;
    }

    .custom_input_field_class_view {
        .v-field__input {
            background: #F7F7F7 !important;
            color: #05146F !important;
        }
    }

    .custom_input_field_class_view {
        .v-textarea .v-field__input {
            background: #F7F7F7 !important;
            color: #05146F !important;
        }
    }

    .custom_fullscreen_dialog_toolbar {
        position: fixed !important;
        z-index: 999999 !important;
    }

    .custom_card_box {
        font-family: var(--font-family) !important;
        box-shadow: 0px 4px 18px 0px #0000001F !important;
    }

    .user_image_view {
        width: 120px;
        height: 120px;
        margin-left: 0px;
        border: 2px solid #dcdcdc;
        border-radius: 50%;
        padding: 4px !important;
        background-color: white;
    }

    .custom_page_main_title {
        font-size: 1.8rem;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 0.25rem;
        color: #000000;
    }

    .custom_page_main_sub_title {
        font-size: 0.95rem;
        opacity: 0.6;
        color: #000000;
        margin-top: 0;
    }

    .reversal-card {
        background: white !important;
        border: 1px solid #E0E0E0 !important;
        overflow: hidden;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;

        &:hover {
            box-shadow: 0 8px 16px rgba(128, 0, 128, 0.15) !important;
        }
    }

    .card-content {
        flex: 1;
    }

    .area-invoice-text {
        display: inline-flex;
        align-items: center;
        line-height: 1.2;

        .invoice-dot {
            display: inline-block;
            vertical-align: middle;
        }
    }

    .purple-text {
        color: #800080 !important;
    }

    .payment-type-box {
        background-color: #E8F5E9 !important;
        border: none !important;

        .v-icon {
            font-size: 30px !important;
        }
    }

    .a4-shape {
        border: 1px solid #E0E0E0;
        background-color: #FAFAFA;
        width: 80px !important;
        height: 108px !important;
    }

    .image-container {
        display: flex;
        align-items: flex-start;
    }

    .image-details-section {
        min-height: 96px;
    }

    .reason-box {
        background-color: #F5F5F5 !important;
        border: 1px solid #E0E0E0 !important;
    }

    .reason-fixed-height {
        height: 120px;
        overflow-y: auto;
    }

    .reason-text {
        line-height: 1.5;
        color: #212121;
        display: block;
    }

    .more-details-btn {
        color: #800080 !important;
        font-size: 0.75rem !important;
        font-weight: 600 !important;
        letter-spacing: 0.5px;
        text-transform: uppercase;

        &:hover {
            background-color: rgba(128, 0, 128, 0.08) !important;
        }
    }

    .approve-btn {
        background: #E1BEE7 !important;
        color: #800080 !important;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        font-size: 0.9rem;
        border-radius: 2px !important;
        border-top: 1px solid #CE93D8 !important;
        margin: 0 !important;
        width: 100%;
    }

    .fixed-height-btn {
        height: 50px !important;
        flex-shrink: 0;

        &:hover {
            background: #800080 !important;
            color: white !important;
        }
    }

    .text-h6 {
        font-size: 1.1rem !important;
        line-height: 1.4;
    }

    .text-body-1 {
        font-size: 0.95rem !important;
    }

    .text-body-2 {
        font-size: 0.85rem !important;
    }

    .text-caption {
        font-size: 0.75rem !important;
    }

    .v-divider {
        border-color: rgba(0, 0, 0, 0.12) !important;
        opacity: 0.8;
    }

    .v-select {
        .v-field {
            font-size: 0.85rem;
        }
    }

    .v-col {
        display: flex;
    }

    .reversal-card {
        flex: 1;
    }

    .v-dialog {
        .v-card {
            .v-card.color-#F0F0F0 {
                background-color: #F0F0F0;
            }
        }
    }

    .custom_btn_primary {
        background-color: #800080 !important;
        color: white !important;
        font-weight: 600;

        &:hover {
            background-color: #600060 !important;
        }
    }

    .custom_btn_secondary {
        color: #757575 !important;
        border: 1px solid #BDBDBD !important;
        background-color: white !important;
        font-weight: 600;

        &:hover {
            background-color: #F5F5F5 !important;
        }
    }

    @media (max-width: 960px) {
        .v-col-md-4 {
            flex: 0 0 100%;
            max-width: 100%;
        }
    }
}

@media (min-width: 320px) {}

@media (min-width: 480px) {}

@media (min-width: 768px) {}

@media (min-width: 1024px) {}

@media (min-width: 1280px) {}

@media (min-width: 1366px) {}

@media (min-width: 1920px) {}
</style>